import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DotsThreeOutline, X, type Icon } from '@phosphor-icons/react';

export interface MobileNavEintrag { id: string; label: string; icon: Icon; badge?: number }

/**
 * Schwebende untere Leiste (Handoff Dokumente §7): fest am unteren Rand, zentriert, Glas, Radius 30, Padding 6.
 * Hochkant 64 px mit Text, quer 56 px nur Icons. Einträge ≥ 64 px breit (ab 6 Knöpfen ≥ 54 px). „Mehr“ öffnet das Bereiche-Sheet.
 */
export function MobileNav({ eintraege, aktivId, quer, mehr, mehrOffen, onMehr, onWahl }: {
  eintraege: readonly MobileNavEintrag[];
  aktivId: string | null;
  quer: boolean;
  /** alle Bereiche für das Mehr-Sheet */
  mehr: readonly MobileNavEintrag[];
  mehrOffen: boolean;
  onMehr: (offen: boolean) => void;
  onWahl: (id: string) => void;
}) {
  const hoehe = quer ? 56 : 64;
  const iconGroesse = quer ? 20 : 22;
  // Mit „Mehr“ mehr als 5 Knöpfe (z. B. inkl. Tickets): schmaler, damit die Leiste auf 390 px passt
  const eng = eintraege.length + 1 > 5;
  const knopf = (e: MobileNavEintrag & { aktiv: boolean; onClick: () => void }) => (
    <button key={e.id} type="button" onClick={e.onClick} aria-label={e.label} aria-current={e.aktiv ? 'page' : undefined} title={quer ? e.label : undefined}
      className="relative flex flex-col items-center justify-center cursor-pointer"
      style={{
        minWidth: eng ? 54 : 64, height: hoehe - 12, padding: eng ? '0 4px' : '0 8px', gap: 3, borderRadius: 24, border: 'none',
        background: e.aktiv ? 'var(--acc-soft)' : 'transparent', color: e.aktiv ? 'var(--acc)' : 'var(--ink2)',
      }}>
      <e.icon weight={e.aktiv ? 'fill' : 'regular'} size={iconGroesse} />
      {!quer && <span style={{ fontSize: 10, fontWeight: e.aktiv ? 800 : 600, lineHeight: 1 }}>{e.label}</span>}
      {!!e.badge && (
        <span className="immo-zahl flex items-center justify-center" aria-label={`${e.badge} offen`}
          style={{ position: 'absolute', top: 4, right: 10, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, fontSize: 10, fontWeight: 800, background: 'var(--bad)', color: '#fff' }}>
          {e.badge > 999 ? '999+' : e.badge}
        </span>
      )}
    </button>
  );
  return (
    <>
      <nav aria-label="Bereiche" className="immo-glas flex items-center"
        style={{
          position: 'fixed', left: 0, right: 0, margin: '0 auto', width: 'max-content', maxWidth: 'calc(100vw - 16px)',
          bottom: `calc(${quer ? 8 : 14}px + env(safe-area-inset-bottom))`, zIndex: 90, height: hoehe, padding: 6, gap: 2, borderRadius: 30,
          overflowX: 'auto',
        }}>
        {eintraege.map(e => knopf({ ...e, aktiv: e.id === aktivId, onClick: () => onWahl(e.id) }))}
        {knopf({ id: '__mehr', label: 'Mehr', icon: DotsThreeOutline, aktiv: mehrOffen, onClick: () => onMehr(!mehrOffen) })}
      </nav>
      {mehrOffen && <MehrSheet eintraege={mehr} aktivId={aktivId} onWahl={id => { onMehr(false); onWahl(id); }} onSchliessen={() => onMehr(false)} />}
    </>
  );
}

/** Sheet „Bereiche“ von unten: 10 px Abstand, Radius 28, abgedunkelter und weichgezeichneter Hintergrund. */
/** Sheet von unten (Handoff Dokumente §7 / E-Mails Screen 31): abgedunkelt, Radius 28, Escape und Klick daneben schließen. */
export function Sheet({ titel, untertitel, onSchliessen, children, maxHoehe = '75vh' }: {
  titel: string; untertitel?: string; onSchliessen: () => void; children: React.ReactNode; maxHoehe?: string;
}) {
  const schliessen = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    schliessen.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSchliessen]);
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={titel} className="fixed inset-0" style={{ zIndex: 120, fontFamily: "'Manrope', -apple-system, sans-serif" }}>
      <div aria-hidden onClick={onSchliessen} className="absolute inset-0" style={{ background: 'rgba(10,18,30,.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} />
      <div className="immo-glas immo-side-in absolute flex flex-col"
        style={{ left: 10, right: 10, bottom: 'calc(10px + env(safe-area-inset-bottom))', maxHeight: maxHoehe, borderRadius: 28, padding: 16, gap: 12, overflowY: 'auto', color: 'var(--ink)' }}>
        <div className="flex items-center justify-between" style={{ gap: 10 }}>
          <span className="flex flex-col min-w-0">
            <span style={{ fontSize: 17, fontWeight: 800 }}>{titel}</span>
            {untertitel && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{untertitel}</span>}
          </span>
          <button ref={schliessen} type="button" onClick={onSchliessen} aria-label="Schließen" className="flex items-center justify-center cursor-pointer shrink-0"
            style={{ width: 44, height: 44, borderRadius: 22, border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
            <X weight="bold" size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function MehrSheet({ eintraege, aktivId, onWahl, onSchliessen }: {
  eintraege: readonly MobileNavEintrag[];
  aktivId: string | null;
  onWahl: (id: string) => void;
  onSchliessen: () => void;
}) {
  return (
    <Sheet titel="Bereiche" onSchliessen={onSchliessen}>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
        {eintraege.map(e => {
          const aktiv = e.id === aktivId;
          return (
            <button key={e.id} type="button" onClick={() => onWahl(e.id)} aria-current={aktiv ? 'page' : undefined}
              className="relative flex flex-col items-center justify-center cursor-pointer min-w-0"
              style={{ height: 78, gap: 6, borderRadius: 18, border: 'none', background: aktiv ? 'var(--acc-soft)' : 'var(--fill)', color: aktiv ? 'var(--acc)' : 'var(--ink)' }}>
              <e.icon weight={aktiv ? 'fill' : 'regular'} size={24} />
              <span className="truncate" style={{ maxWidth: '100%', padding: '0 6px', fontSize: 12, fontWeight: 700 }}>{e.label}</span>
              {!!e.badge && (
                <span className="immo-zahl" style={{ position: 'absolute', top: 6, right: 8, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, fontSize: 10, fontWeight: 800, background: 'var(--bad)', color: '#fff' }}>
                  {e.badge > 999 ? '999+' : e.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}
